import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, ExternalLink } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { isIOS, downloadBlobUrl } from '../utils/downloadFile';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const DietDetailPage = () => {
  const { dietId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [diet, setDiet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const pdfUrlRef = useRef(null);
  const mobile = isIOS();

  useEffect(() => {
    apiFetch(`/api/diets/${dietId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        setDiet(data);
        if (data?.pdf_file) {
          try {
            const base64Data = data.pdf_file.includes(',')
              ? data.pdf_file.split(',')[1]
              : data.pdf_file;
            const byteCharacters = atob(base64Data);
            const bytes = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              bytes[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            pdfUrlRef.current = url;
            setPdfUrl(url);
          } catch (e) {
            console.error('Error converting PDF:', e);
          }
        }
      })
      .catch(() => setDiet(null))
      .finally(() => setLoading(false));

    return () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    };
  }, [dietId]);

  const handleDownload = async () => {
    if (!diet?.pdf_file) return;

    if (Capacitor.isNativePlatform()) {
      try {
        const base64Data = diet.pdf_file.includes(',')
          ? diet.pdf_file.split(',')[1]
          : diet.pdf_file;
        const filename = `${diet.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
        const { uri } = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Cache,
        });
        await Share.share({ title: diet.title, url: uri });
      } catch (e) {
        console.error('Error sharing PDF natively:', e);
      }
      return;
    }

    if (!pdfUrl) return;
    downloadBlobUrl(pdfUrl, `${diet.title}.pdf`);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  if (!diet) return (
    <div className="page-container">
      <button className="btn btn-secondary mb-4" style={{ gap: '6px' }} onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> {t('common.back')}
      </button>
      <p style={{ color: 'var(--muted-foreground)' }}>{t('diets.not_found')}</p>
    </div>
  );

  return (
    <div className="page-container animate-fade-in">
      <div className="header" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(-1)}
            style={{ flexShrink: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="header-title">{diet.title}</h1>
            <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '2px' }}>
              {formatDate(diet.created_at)}
            </p>
          </div>
        </div>
        <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
      </div>

      {diet.notes && (
        <div className="card mb-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <FileText size={16} style={{ color: 'var(--primary)' }} />
            <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)' }}>
              {t('diets.notes_label')}
            </p>
          </div>
          <p style={{ fontSize: '15px', color: 'var(--foreground)', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
            {diet.notes}
          </p>
        </div>
      )}

      {/* PDF viewer — iframe disabled on iOS (renders blank in PWA) */}
      {pdfUrl ? (
        mobile ? (
          <div className="card mb-3" style={{ textAlign: 'center', padding: '36px 20px' }}>
            <FileText size={52} style={{ color: 'var(--primary)', marginBottom: '14px' }} />
            <p style={{ fontSize: '16px', fontWeight: '700', color: 'var(--foreground)', marginBottom: '6px' }}>
              {diet.title}
            </p>
            <p style={{ fontSize: '13px', color: 'var(--muted-foreground)', marginBottom: '20px', lineHeight: '1.5' }}>
              {t('diets.ios_pdf_hint')}
            </p>
            <button
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              onClick={handleDownload}
            >
              <ExternalLink size={16} />
              {t('diets.open_pdf_btn')}
            </button>
          </div>
        ) : (
          <div className="card mb-3" style={{ padding: '0', overflow: 'hidden', borderRadius: '8px' }}>
            <iframe
              src={pdfUrl}
              style={{ width: '100%', height: '65vh', border: 'none', display: 'block' }}
              title={diet.title}
            />
          </div>
        )
      ) : (
        <div className="card mb-3" style={{ textAlign: 'center', padding: '32px 16px' }}>
          <FileText size={40} style={{ color: 'var(--muted-foreground)', marginBottom: '12px' }} />
          <p style={{ color: 'var(--muted-foreground)', fontSize: '14px' }}>{t('diets.pdf_unavailable')}</p>
        </div>
      )}

      {/* Download button — hidden on iOS when already showing the open button inside the card */}
      {pdfUrl && !mobile && (
        <button
          className="btn btn-primary w-full mb-4"
          onClick={handleDownload}
        >
          <Download size={18} />
          {t('diets.download_btn')}
        </button>
      )}
    </div>
  );
};

export default DietDetailPage;
